/**
 * CABProgressSidebar — Vertical step progress indicator for CAB generation.
 *
 * Shows each pipeline phase as a vertical step with:
 * - Upload step (frontend-only, first step)
 * - Pending state (gray circle with number)
 * - Active state (blue pulsing circle)
 * - Completed state (green circle with checkmark)
 * - Detail text under active step (e.g., "Parsing file 2/5")
 */

import React from 'react';
import { Text, Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import type { CABPhasesInfo, CABPhaseStatus, CABSectionGenerationPhase } from '../../types/cab';

interface CABProgressSidebarProps {
  phases: CABPhasesInfo | null;
  visible: boolean;
  sessionStatus: string;
}

interface PhaseDefinition {
  id: string;
  label: string;
  phaseKey: keyof CABPhasesInfo;
}

/** Pipeline phases before section generation. */
const PIPELINE_PHASES: PhaseDefinition[] = [
  { id: 'file_processing', label: 'Reading Files', phaseKey: 'file_processing' },
  { id: 'code_analysis', label: 'Analyzing Code', phaseKey: 'code_analysis' },
  { id: 'skeleton_map', label: 'Discovering Sections', phaseKey: 'skeleton_map' },
  { id: 'skeleton_reduce', label: 'Organizing Sections', phaseKey: 'skeleton_reduce' },
  { id: 'skeleton_assemble', label: 'Finalizing Document Outline', phaseKey: 'skeleton_assemble' },
];

/** Individual CAB sections shown as separate progress steps. */
const SECTION_DEFINITIONS: { id: string; label: string }[] = [
  { id: 'projectIdentity', label: 'Project Identity' },
  { id: 'roles', label: 'User Roles' },
  { id: 'dataModel', label: 'Data Model' },
  { id: 'screens', label: 'Screens & UI' },
  { id: 'workflows', label: 'Workflows' },
  { id: 'reporting', label: 'Reports' },
  { id: 'integrations', label: 'Integrations' },
  { id: 'rules', label: 'Business Rules' },
  { id: 'userStories', label: 'User Stories' },
  { id: 'suggestedQuestions', label: 'Suggested Questions' },
];

function getPhaseStatus(phases: CABPhasesInfo | null, phaseKey: keyof CABPhasesInfo): CABPhaseStatus {
  if (!phases) return 'pending';
  const phase = phases[phaseKey];
  if (!phase) return 'pending';
  return (phase as any).status || 'pending';
}

function getPhaseProgress(phases: CABPhasesInfo | null, phaseKey: keyof CABPhasesInfo): string | null {
  if (!phases) return null;
  const phase = phases[phaseKey];
  if (!phase) return null;
  
  const progress = (phase as any).progress;
  if (!progress) return null;
  
  if (progress.current_step) return progress.current_step;
  if (progress.total > 0) {
    return `${progress.completed} / ${progress.total}`;
  }
  return null;
}

/** Get failed count and latest error message for a phase. */
function getPhaseErrors(phases: CABPhasesInfo | null, phaseKey: keyof CABPhasesInfo): { failed: number; latestError: string | null } {
  if (!phases) return { failed: 0, latestError: null };
  const phase = phases[phaseKey];
  if (!phase) return { failed: 0, latestError: null };

  const progress = (phase as any).progress;
  if (!progress) return { failed: 0, latestError: null };

  const failed = progress.failed || 0;
  if (failed === 0) return { failed: 0, latestError: null };

  const errors = progress.errors as { message: string }[] | undefined;
  const latestError = errors?.length ? errors[errors.length - 1].message : null;
  return { failed, latestError };
}

/** Get the duration_seconds for a completed phase. */
function getPhaseDuration(phases: CABPhasesInfo | null, phaseKey: keyof CABPhasesInfo): number | null {
  if (!phases) return null;
  const phase = phases[phaseKey];
  if (!phase) return null;
  return (phase as any).duration_seconds ?? null;
}

/** Format a duration in seconds to a human-readable string (matches Build Mode). */
function formatDuration(seconds: number): string {
  if (seconds < 1) return `${seconds.toFixed(1)}s`;
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const mins = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return `${mins}m ${remainingSeconds}s`;
}

/** Get the status of an individual section within section_generation. */
function getSectionStatus(phases: CABPhasesInfo | null, sectionId: string): CABPhaseStatus {
  if (!phases) return 'pending';
  const sectionPhase = phases.section_generation as CABSectionGenerationPhase | undefined;
  if (!sectionPhase) return 'pending';

  // If the entire section_generation phase hasn't started, all sections are pending
  const phaseStatus = sectionPhase.status;
  if (!phaseStatus || phaseStatus === 'pending') return 'pending';

  const sectionInfo = sectionPhase.sections?.[sectionId];
  if (!sectionInfo) {
    // Section not initialized yet - show as pending (backend will initialize soon)
    return 'pending';
  }
  return sectionInfo.status || 'pending';
}

/** Check if section generation phase has started (to decide whether to show section steps). */
function isSectionPhaseActive(phases: CABPhasesInfo | null): boolean {
  if (!phases) return false;
  const sectionPhase = phases.section_generation as CABSectionGenerationPhase | undefined;
  if (!sectionPhase) return false;
  const status = sectionPhase.status;
  return status === 'running' || status === 'completed' || status === 'error';
}

export const CABProgressSidebar: React.FC<CABProgressSidebarProps> = ({
  phases,
  visible,
  sessionStatus,
}) => {
  if (!visible) return null;

  return (
    <div className="w-[200px] bg-bg-primary rounded-bg-v2-lg container-shadow-sm p-bg-v2-lg overflow-y-auto shrink-0 transition-all duration-300">
      <Text
        variant="text-xs"
        weight="semibold"
        className="text-text-tertiary uppercase tracking-wider mb-bg-v2-lg"
      >
        Progress
      </Text>

      <div className="flex flex-col gap-bg-v2-sm relative">
        {/* Vertical connecting line */}
        <div
          className="absolute left-[13px] top-7 bottom-7 w-0.5 bg-border-secondary z-0"
        />

        {/* ─── Pipeline Phases (before section generation) ─── */}
        {PIPELINE_PHASES.map((def, index) => {
          const status = getPhaseStatus(phases, def.phaseKey);
          const progressText = getPhaseProgress(phases, def.phaseKey);
          const durationSec = getPhaseDuration(phases, def.phaseKey);
          const { failed, latestError } = getPhaseErrors(phases, def.phaseKey);

          const isActive = status === 'running' && sessionStatus !== 'paused';
          const isRunningOrPaused = status === 'running'; // Show progress even when paused
          const isCompleted = status === 'completed';
          const isSkipped = status === 'skipped';
          const isError = status === 'error';
          const stepNumber = index + 1;

          return (
            <div
              key={def.id}
              className="flex items-start gap-bg-v2-md relative z-[1] py-bg-v2-xs"
            >
              {/* Circle with optional spinner */}
              <div className="relative shrink-0 w-7 h-7">
                {isActive && (
                  <div className="absolute inset-[-3px] rounded-full border-2 border-transparent border-t-bg-brand-solid animate-spin" />
                )}
                <div
                  className={`
                    w-7 h-7 rounded-full flex items-center justify-center
                    font-bg-v2-semibold text-bg-v2-xs transition-all duration-300
                    ${isCompleted
                      ? 'bg-bg-success-solid text-text-white border-2 border-bg-success-solid'
                      : isSkipped
                        ? 'bg-bg-tertiary text-text-tertiary border-2 border-border-secondary'
                        : isRunningOrPaused
                          ? 'bg-bg-brand-solid text-text-white border-2 border-bg-brand-solid'
                          : isError
                            ? 'bg-bg-error-primary text-text-error-primary border-2 border-text-error-primary'
                            : 'bg-bg-primary text-text-tertiary border-2 border-border-primary'
                    }
                  `}
                >
                  {isCompleted ? (
                    <Icon name={IconName.Check} size="xs" inheritColor />
                  ) : isSkipped ? (
                    <Icon name={IconName.Skip} size="xs" inheritColor />
                  ) : isError ? (
                    <span>!</span>
                  ) : (
                    <span>{stepNumber}</span>
                  )}
                </div>
              </div>

              {/* Label + progress detail + duration */}
              <div className="pt-[3px]">
                <Text
                  variant="text-xs"
                  weight={isRunningOrPaused ? 'semibold' : 'medium'}
                  className={
                    isRunningOrPaused
                      ? 'text-text-brand-primary'
                      : isCompleted
                        ? 'text-text-secondary'
                        : isError
                          ? 'text-text-error-primary'
                          : 'text-text-tertiary'
                  }
                >
                  {def.label}{isSkipped ? ' (Not Applicable)' : ''}
                </Text>

                {isRunningOrPaused && progressText && (
                  <Text variant="text-xs" className="text-text-tertiary mt-bg-v2-xxs">
                    {progressText}
                    {sessionStatus === 'paused' && ' (paused)'}
                  </Text>
                )}
                {isCompleted && durationSec != null && (
                  <Text variant="text-xs" className="text-text-quaternary mt-bg-v2-xxs">
                    {formatDuration(durationSec)}
                  </Text>
                )}
                {failed > 0 && (
                  <Text variant="text-xs" className="text-text-error-primary mt-bg-v2-xxs">
                    {failed} batch{failed > 1 ? 'es' : ''} failed
                  </Text>
                )}
                {failed > 0 && latestError && (
                  <Text
                    variant="text-xs"
                    className="text-text-error-primary mt-bg-v2-xxs leading-snug break-words max-w-[140px]"
                  >
                    {latestError.length > 100 ? latestError.slice(0, 100) + '...' : latestError}
                  </Text>
                )}
              </div>
            </div>
          );
        })}

        {/* ─── "Writing Sections" header ─── */}
        {(() => {
          const sectionPhaseStatus = getPhaseStatus(phases, 'section_generation');
          const sectionDurationSec = getPhaseDuration(phases, 'section_generation');
          const isSectionActive = sectionPhaseStatus === 'running' && sessionStatus !== 'paused';
          const isSectionRunningOrPaused = sectionPhaseStatus === 'running'; // Show progress even when paused
          const isSectionCompleted = sectionPhaseStatus === 'completed';
          const isSectionError = sectionPhaseStatus === 'error';
          const showSections = isSectionPhaseActive(phases);
          const stepNumber = PIPELINE_PHASES.length + 1;

          return (
            <>
              <div className="flex items-start gap-bg-v2-md relative z-[1] py-bg-v2-xs">
                <div className="relative shrink-0 w-7 h-7">
                  {isSectionActive && (
                    <div className="absolute inset-[-3px] rounded-full border-2 border-transparent border-t-bg-brand-solid animate-spin" />
                  )}
                  <div
                    className={`
                      w-7 h-7 rounded-full flex items-center justify-center
                      font-bg-v2-semibold text-bg-v2-xs transition-all duration-300
                      ${isSectionCompleted
                        ? 'bg-bg-success-solid text-text-white border-2 border-bg-success-solid'
                        : isSectionRunningOrPaused
                          ? 'bg-bg-brand-solid text-text-white border-2 border-bg-brand-solid'
                          : isSectionError
                            ? 'bg-bg-error-primary text-text-error-primary border-2 border-text-error-primary'
                            : 'bg-bg-primary text-text-tertiary border-2 border-border-primary'
                      }
                    `}
                  >
                    {isSectionCompleted ? (
                      <Icon name={IconName.Check} size="xs" inheritColor />
                    ) : isSectionError ? (
                      <span>!</span>
                    ) : (
                      <span>{stepNumber}</span>
                    )}
                  </div>
                </div>
                <div className="pt-[3px]">
                  <Text
                    variant="text-xs"
                    weight={isSectionRunningOrPaused ? 'semibold' : 'medium'}
                    className={
                      isSectionRunningOrPaused
                        ? 'text-text-brand-primary'
                        : isSectionCompleted
                          ? 'text-text-secondary'
                          : isSectionError
                            ? 'text-text-error-primary'
                            : 'text-text-tertiary'
                    }
                  >
                    Generating Sections{sessionStatus === 'paused' && isSectionRunningOrPaused ? ' (paused)' : ''}
                  </Text>
                  {isSectionCompleted && sectionDurationSec != null && (
                    <Text variant="text-xs" className="text-text-quaternary mt-bg-v2-xxs">
                      {formatDuration(sectionDurationSec)}
                    </Text>
                  )}
                </div>
              </div>

              {/* ─── Individual Section Sub-steps ─── */}
              {showSections && SECTION_DEFINITIONS.map((sec) => {
                const secStatus = getSectionStatus(phases, sec.id);

                const secActive = secStatus === 'running' && sessionStatus !== 'paused';
                const secRunningOrPaused = secStatus === 'running'; // Show progress even when paused
                const secCompleted = secStatus === 'completed';
                const secSkipped = secStatus === 'skipped';
                const secError = secStatus === 'error';
                const secPending = secStatus === 'pending';

                return (
                  <div
                    key={sec.id}
                    className="flex items-start gap-bg-v2-sm relative z-[1] py-[2px] ml-[18px]"
                  >
                    {/* Smaller indicator dot */}
                    <div className="relative shrink-0 w-4 h-4 mt-[2px]">
                      {secActive && (
                        <div className="absolute inset-[-2px] rounded-full border-[1.5px] border-transparent border-t-bg-brand-solid animate-spin" />
                      )}
                      <div
                        className={`
                          w-4 h-4 rounded-full flex items-center justify-center
                          transition-all duration-300
                          ${secCompleted
                            ? 'bg-bg-success-solid text-text-white border-[1.5px] border-bg-success-solid'
                            : secRunningOrPaused
                              ? 'bg-bg-brand-solid text-text-white border-[1.5px] border-bg-brand-solid'
                              : secError
                                ? 'bg-bg-error-primary text-text-error-primary border-[1.5px] border-text-error-primary'
                                : secSkipped
                                  ? 'bg-bg-tertiary text-text-quaternary border-[1.5px] border-border-tertiary'
                                  : 'bg-bg-primary text-text-quaternary border-[1.5px] border-border-secondary'
                          }
                        `}
                      >
                        {secCompleted && (
                          <Icon name={IconName.Check} size="xs" inheritColor />
                        )}
                        {secSkipped && (
                          <Icon name={IconName.Skip} className="w-2 h-2" inheritColor />
                        )}
                        {secError && (
                          <span className="text-[8px] font-bold">!</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <Text
                        variant="text-xs"
                        weight={secRunningOrPaused ? 'semibold' : 'regular'}
                        className={
                          secRunningOrPaused
                            ? 'text-text-brand-primary'
                            : secCompleted
                              ? 'text-text-secondary'
                              : secError
                                ? 'text-text-error-primary'
                                : secSkipped
                                  ? 'text-text-quaternary'
                                  : 'text-text-quaternary'
                        }
                      >
                        {sec.label}{secSkipped ? ' (Skipped)' : sessionStatus === 'paused' && secRunningOrPaused ? ' (paused)' : ''}
                      </Text>
                    </div>
                  </div>
                );
              })}
            </>
          );
        })()}
      </div>
    </div>
  );
};
